import { useState } from 'react';

import { Button } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { YStack } from '@/components/ui/layout/layout';
import { SearchInput } from '@/components/ui/SearchInput';
import { cn } from '@/lib/utils';

import { PersonCell } from '../components/person-cell';
import { filterPeople, withoutRequestOnly } from '../filter-people';
import { usePeopleWikiQuery } from '../people-wiki.query';

import { useMergePeopleMutation } from './edits.query';

import type { WikiPersonDetail } from '../people-wiki.types';

export const MergePersonDialog = ({
  person,
  onClose,
}: {
  person: WikiPersonDetail;
  onClose: () => void;
}) => {
  const { data: people = [], isLoading } = usePeopleWikiQuery();
  const [query, setQuery] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const { mutate: merge, isPending } = useMergePeopleMutation(person.id);
  const candidates = filterPeople(
    withoutRequestOnly(people).filter(({ id }) => id !== person.id),
    query
  );
  const target = people.find(({ id }) => id === targetId);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Merge {person.name} with…</Dialog.Title>
          <Dialog.Description>
            Pick the person who is the same as {person.name}. Their accounts,
            facts, notes and names all come together on that person, and nothing
            is dropped.
          </Dialog.Description>
        </Dialog.Header>
        <YStack className="gap-3">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Search people..."
            aria-label="Search people to merge with"
            autoFocus
          />
          <YStack className="max-h-72 overflow-y-auto rounded-lg border border-border">
            {candidates.length === 0 ? (
              <span className="px-3 py-6 text-center text-sm text-foreground-muted">
                {isLoading ? 'Loading people…' : 'No people match your search'}
              </span>
            ) : (
              candidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  aria-pressed={candidate.id === targetId}
                  disabled={isPending}
                  className={cn(
                    'px-3 py-2 text-left transition-colors hover:bg-surface-hover focus-ring',
                    candidate.id === targetId && 'bg-surface-active'
                  )}
                  onClick={() => setTargetId(candidate.id)}
                >
                  <PersonCell person={candidate} />
                </button>
              ))
            )}
          </YStack>
        </YStack>
        <Dialog.Footer>
          <Button variant="secondary" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            isLoading={isPending}
            disabled={target === undefined}
            onClick={() =>
              targetId &&
              merge(
                { personIds: [targetId, person.id] },
                { onSuccess: onClose }
              )
            }
          >
            {target ? `Merge into ${target.name}` : 'Merge'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
